import {
  ErrorDialogContext,
  errorHandledSync,
} from "@/shared/context/error-context";
import { TmpTrades } from "@/features/accounts/ocr/hooks/use-results-table-states";
import { GetTmpTradesService } from "@/features/accounts/ocr/services/get-tmp-trades-service";
import { PostTradesService } from "@/features/accounts/ocr/services/post-trades-service";
import { useContext } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { OcrDetails } from "@/features/accounts/ocr/services/post-trades-model";

export const useResultsTable = () => {
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  const loadingCtx = useContext(LoadingModalContext);

  const getTmpTradesService = new GetTmpTradesService();
  const postTradesService = new PostTradesService();

  /*--------------------
    イベント関連
  --------------------*/
  // OCR読み取り結果の編集用下書き管理ロジック↓↓
  const buildDraftKey = (
    ocrAccountId: string,
    ocrProcessId: string,
  ) => {
    return `ocr_draft_${ocrAccountId}_${ocrProcessId}`;
  };

  // localStorageからtmpTradesByImageを取得
  const getTmpTradesByImage = (
    ocrAccountId: string, 
    ocrProcessId: string
  ): Record<string, TmpTrades[]> => {
    const draftKey = buildDraftKey(ocrAccountId, ocrProcessId);
    const raw = localStorage.getItem(draftKey);
    if (raw) {
      return JSON.parse(raw);
    }
    return {};
  };

  // localStorageからtmpTradesByImageを取得
  const loadDraft = (key: string) => {
    const raw = localStorage.getItem(key);
    if (raw) {
      return JSON.parse(raw);
    }
  };

    // localStorageにtmpTradesByImageを保存
  const saveDraft = (key: string, data: Record<string, TmpTrades[]> | undefined) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  // 削除はすべての画像が完了次第で消す
  // const removeDraft = (key: string) => {
    // localStorage.removeItem(key);
  // };
  // OCR読み取り結果の編集用下書き管理ロジック↑↑
  // OCR読み取り結果一覧取得
  const setTmpTrades = async (
    accountGroupId: string,
    ocrAccountId: string,
    ocrProcessId: string,
  ) => {
    // 下書きデータがあればそちらを優先して読み込み
    const draftKey = buildDraftKey(ocrAccountId, ocrProcessId);
    const draft = loadDraft(draftKey);
    if (draft) {
      // 既にlocalStorageに保存済み
      return;
    }

    try {
      const res = await getTmpTradesService.GetTmpTrades({
        account_group_id: accountGroupId,
        ocr_account_id : ocrAccountId,
        ocr_process_id: ocrProcessId,
      });
      const grouped: Record<string, TmpTrades[]> = {};
      res.forEach((trade) => {
        const fileName = trade.file_name;
        if (!grouped[fileName]) {
          grouped[fileName] = [];
        }

        grouped[fileName].push({
          ocrAccountId: trade.ocr_account_id,
          tradeId: trade.trade_id,
          ocrProcessId: trade.ocr_process_id,
          fileName,
          date: trade.date,
          sortOrder: trade.sort_order,
          balance: trade.balance,
          summary: trade.summary,
          remark: trade.remark,
          deposit: trade.trade_type === "1" ? trade.amount : "",
          withdrawal: trade.trade_type === "2" ? trade.amount : "",
        });
      });

      // 並び替え
      Object.keys(grouped).forEach((fileName) => {
        grouped[fileName].sort((a, b) => {
          // 日付で比較
          if (a.date !== b.date) {
            return a.date.localeCompare(b.date);
          }
          // 並び順で比較
          return a.sortOrder - b.sortOrder;
        });
      });

      saveDraft(draftKey, grouped);
    } catch (error) {
      // エラーハンドリング
      console.log("setTmpTrades", error);
      throw error;
    }
  };

  // 編集内容自動保存
  const updateTmpTradesByImage = (
    updater: (prev: Record<string, TmpTrades[]>) => Record<string, TmpTrades[]>,
    draftKey: string,
  ) => {
    // localStorageから現在値を取得
    const prev = loadDraft(draftKey) || {};
    const updated = updater(prev);
    saveDraft(draftKey, updated);
    // 状態はlocalStorageのみで管理
  };

  // OCR読み取り結果本登録処理
  const confirmedTrades = async (
    accountGroupId: string,
    ocrAccountId: string,
    ocrProcessId: string,
    fileName: string,
    tradeList: OcrDetails[],
    duplicateProcessing?: string,
  ) => {
    loadingCtx.setIsLoading(true);
    try {
      return await postTradesService.PostTrades({
        account_group_id: accountGroupId,
        ocr_account_id : ocrAccountId,
        ocr_process_id: ocrProcessId,
        file_name: fileName,
        trade_list: tradeList,
        ...(duplicateProcessing !== undefined && { duplicate_processing: duplicateProcessing }),
      });
    } catch (error) {
      // エラーハンドリング
      console.log("confirmedTrades", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  return {
    buildDraftKey,
    getTmpTradesByImage,
    updateTmpTradesByImage,
    setTmpTrades: errorHandledSync(setTmpTrades, setError),
    confirmedTrades: errorHandledSync(confirmedTrades, setError),
  };
}
